import { useState, useEffect } from 'react';
import { Form, Checkbox, Col, Row, Flex, Typography, Image, Alert } from 'antd';
import ArrowLeftIcon from "../../../assets/icons/arrow-left.svg";
import CustomInput from '../../../components/CustomInput/Input';
import CustomButton from '../../../components/CustomButton/Button';
import { accNumberRules, ifscRules, nameRules } from '../../../helpers/helpers';
import { addSecondaryBank, clearProfileError, getBankDetailsByOrgId, verifyIfsc } from '../../../redux/slices/ProfileSlice';
import { useDispatch, useSelector } from "react-redux";
import { BTN_TXT, ERROR_MSGS, GLOBAL_CONSTANTS, TITLE_TXT } from '../../../constants/constants';

const { Title } = Typography;

const AddBankDetails = ({ goBack }) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { ifscData, addBankLoading, addBankError } = useSelector((state) => state.profile);
    const [consentGiven, setConsentGiven] = useState(false);

    useEffect(() => {
        if (ifscData?.bankName) {
            form.setFieldsValue({ bankName: ifscData?.bankName });
        }
        return () => {
            dispatch(clearProfileError());
        };
    }, [ifscData, form, dispatch]);

    const handleIfscBlur = async () => {
        const ifscCode = form.getFieldValue("ifsc");
        if (ifscCode && ifscCode.length === 11) {
            await dispatch(verifyIfsc({ ifscCode: ifscCode }));
        }
    };

    const validateAccountNumbers = ({ getFieldValue }) => ({
        validator(_, value) {
            if (!value || getFieldValue('accNumber') === value) {
                return Promise.resolve();
            }
            return Promise.reject(new Error(ERROR_MSGS.ACC_NUM_ERR_MSG));
        },
    });

    const handleSubmit = async (values) => {
        const payload = {
            ifscCode: values.ifsc,
            accNum: values.accNumber,
            accHolderName: values.accHolderName,
        }

        const res = await dispatch(addSecondaryBank(payload))

        if (res.payload.statusCode === 201) {
            form.resetFields();
            goBack();
            dispatch(getBankDetailsByOrgId(user?.orgId));
        }
    };

    return (
        <div className='investor-details-container'>
            <div className='header-container'>
                <Flex gap={20} align='center'>
                    <Image
                        src={ArrowLeftIcon}
                        alt="Arrow Left"
                        onClick={goBack}
                        preview={false}
                    />
                    <Title level={4}>{TITLE_TXT.ADD_NEW_BANK_DETS}</Title>
                </Flex>
            </div>
            <div className="details-container">
                <Form
                    layout="vertical"
                    onFinish={handleSubmit}
                    form={form}
                    initialValues={{ remember: true }}
                    className='change-password-form'
                >
                    <Row>
                        <Col xs={24} md={18} lg={12}>
                            <CustomInput
                                label="IFSC Code"
                                name="ifsc"
                                type="text"
                                rules={ifscRules}
                                onBlur={handleIfscBlur}
                            />
                            <CustomInput
                                label="Bank Name"
                                name="bankName"
                                type="text"
                                disabled
                            />
                            <CustomInput
                                label="Account Holder Name"
                                name="accHolderName"
                                type="text"
                                rules={nameRules("Account Holder")}
                            />

                            <CustomInput
                                label="Account Number"
                                name="accNumber"
                                type="number"
                                rules={accNumberRules}
                            />
                            <CustomInput
                                label="Re-type Account Number"
                                name="retypeAccNumber"
                                type="number"
                                rules={[validateAccountNumbers]}
                            />
                            <Form.Item
                                rules={[{ required: true, message: ERROR_MSGS.ACCP_CONCENT_ERR_MSG }]}
                            >
                                <Checkbox checked={consentGiven} onChange={(e) => setConsentGiven(e.target.checked)}>
                                    {GLOBAL_CONSTANTS.CONCENT_PROV}
                                </Checkbox>
                            </Form.Item>
                            {addBankError && <Alert style={{ marginBottom: 20 }} message={addBankError} type="error" showIcon />}
                            <CustomButton
                                type="primary"
                                htmlType="submit"
                                block
                                disabled={!consentGiven}
                                loading={addBankLoading}
                            >
                                {BTN_TXT.ADD_NEW_BANK}
                            </CustomButton>
                        </Col>
                    </Row>
                </Form>
            </div>
        </div>
    );
};

export default AddBankDetails;
